import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCampaignEvents, type CampaignEvent, type CampaignEventType } from '../api/campaigns';
import { formatAbsoluteTime } from '@/utils/date';

interface ActivityFeedProps {
  campaignId: string;
}

// Event type icons and colors
const EVENT_CONFIG: Record<CampaignEventType, { icon: string; label: string }> = {
  media_plan_imported: { icon: '📋', label: 'Plan Import' },
  media_plan_approved: { icon: '✅', label: 'Plan Approved' },
  fordshare_id_set: { icon: '🔗', label: 'FordShare ID' },
  asset_uploaded: { icon: '📥', label: 'Asset Upload' },
  asset_approved: { icon: '✅', label: 'Asset Approved' },
  preview_link_added: { icon: '🔗', label: 'Preview Link' },
  preview_approved: { icon: '👍', label: 'Preview Approved' },
  campaign_live: { icon: '🚀', label: 'Live' },
  activation_workflow_reset: { icon: '🔄', label: 'Activation Reset' },
  comment_added: { icon: '💬', label: 'Comment' },
  archived: { icon: '📦', label: 'Archived' },
  unarchived: { icon: '📂', label: 'Unarchived' },
  field_updated: { icon: '✏️', label: 'Updated' },
};

function formatTimestamp(dateString: string): string {
  return formatAbsoluteTime(dateString);
}

function getEventConfig(eventType: CampaignEventType) {
  return EVENT_CONFIG[eventType] || { icon: '📌', label: 'Event' };
}

export function ActivityFeed({ campaignId }: ActivityFeedProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['campaign-events', campaignId],
    queryFn: () => getCampaignEvents(campaignId, 50),
  });

  const events = data?.events ?? [];
  const totalCount = data?.total_count ?? 0;

  return (
    <div className="border-b border-gray-200">
      {/* Header - clickable to expand/collapse */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-sm font-medium text-gray-700">
            Recent Activity
            {totalCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">
                {totalCount}
              </span>
            )}
          </span>
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-6 pb-4">
          {isLoading ? (
            <div className="py-4 text-center text-sm text-gray-500">Loading activity...</div>
          ) : isError ? (
            <div className="py-4 text-center text-sm text-red-500">
              Failed to load activity. Please try again later.
            </div>
          ) : events.length === 0 ? (
            <div className="py-4 text-center text-sm text-gray-500">No activity recorded yet</div>
          ) : (
            <div className="max-h-[250px] overflow-y-auto space-y-1">
              {events.map((event) => (
                <ActivityItem key={event.id} event={event} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ActivityItem({ event }: { event: CampaignEvent }) {
  const config = getEventConfig(event.event_type);

  return (
    <div className="flex items-start gap-3 py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors">
      {/* Icon */}
      <span className="text-base flex-shrink-0 mt-0.5" role="img" aria-label={config.label}>
        {config.icon}
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800">{event.message}</p>
        <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
          <span>{formatTimestamp(event.created_at)}</span>
          {event.actor_name && (
            <>
              <span>-</span>
              <span className="truncate">{event.actor_name}</span>
            </>
          )}
          {!event.actor_name && event.actor_email && (
            <>
              <span>-</span>
              <span className="truncate">{event.actor_email.split('@')[0]}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
